import { ReactNode } from "react";
import { Avatar, Button, Descriptions, Modal, Tag, Tooltip } from "antd";
import { CloseOutlined, EyeOutlined } from "@ant-design/icons";
import { COLOR_PRIMARY, COLOR_SECONDARY } from "../../../constants";

export interface DetailItemI {
  label: string;
  value?: ReactNode;
  span?: number;
}

export interface DetailSectionI {
  title: string;
  icon?: ReactNode;
  items: DetailItemI[];
}

interface DetailModalPropsI {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  image?: string | null;
  tag?: { text: string; color?: string } | null;
  sections: DetailSectionI[];
  loading?: boolean;
  footer?: ReactNode;
}

const initials = (text: string) =>
  text
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

const empty = (value: ReactNode) =>
  value === undefined || value === null || value === "" ? (
    <span style={{ color: "#a0a4ad" }}>—</span>
  ) : (
    value
  );

export const DetailModal = ({
  open,
  onClose,
  title,
  subtitle,
  image,
  tag,
  sections,
  loading,
  footer,
}: DetailModalPropsI) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={footer ?? null}
    closeIcon={
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 30,
          height: 30,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #ff4d4f, #cf1322)",
          boxShadow: "0 2px 8px rgba(207, 19, 34, 0.45)",
          border: "2px solid rgba(255,255,255,0.85)",
          color: "#fff",
          fontSize: 13,
        }}
      >
        <CloseOutlined />
      </span>
    }
    styles={{ content: { padding: 0, overflow: "hidden", borderRadius: 10 } }}
    width={640}
    centered
    destroyOnClose
    loading={loading}
  >
    <div
      style={{
        background: COLOR_PRIMARY,
        borderBottom: `4px solid ${COLOR_SECONDARY}`,
        padding: "24px 56px 20px 24px",
        display: "flex",
        alignItems: "center",
        gap: 16,
      }}
    >
      <Avatar
        size={64}
        src={image || undefined}
        style={{
          background: COLOR_SECONDARY,
          color: "#fff",
          fontWeight: 600,
          fontSize: 22,
          border: "2px solid rgba(255,255,255,0.85)",
          flexShrink: 0,
        }}
      >
        {initials(title)}
      </Avatar>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            color: "#fff",
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 1.25,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </div>
        {subtitle ? (
          <div style={{ color: "rgba(255,255,255,0.7)", marginTop: 2 }}>
            {subtitle}
          </div>
        ) : null}
        {tag ? (
          <Tag color={tag.color} style={{ marginTop: 8, marginInlineEnd: 0 }}>
            {tag.text}
          </Tag>
        ) : null}
      </div>
    </div>

    <div
      style={{
        padding: "8px 24px 24px",
        maxHeight: "60vh",
        overflowY: "auto",
      }}
    >
      {sections.map((section) => (
        <div key={section.title} style={{ marginTop: 16 }}>
          <div
            style={{
              color: COLOR_PRIMARY,
              fontWeight: 600,
              fontSize: 13,
              textTransform: "uppercase",
              letterSpacing: 0.6,
              paddingBottom: 6,
              marginBottom: 4,
              borderBottom: `2px solid ${COLOR_SECONDARY}33`,
              display: "flex",
              gap: 8,
              alignItems: "center",
            }}
          >
            {section.icon}
            {section.title}
          </div>
          <Descriptions
            size="small"
            column={{ xs: 1, sm: 2 }}
            colon={false}
            layout="vertical"
            labelStyle={{ color: "#6b7280", fontSize: 12 }}
            contentStyle={{ color: "#111827", fontWeight: 500 }}
          >
            {section.items.map((item) => (
              <Descriptions.Item
                key={item.label}
                label={item.label}
                span={item.span}
              >
                {empty(item.value)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      ))}
    </div>
  </Modal>
);

/** Botón de fila "Ver información" listo para usar en las tablas. */
export const DetailButton = ({ onClick }: { onClick: () => void }) => (
  <Tooltip arrow={false} title="Ver información">
    <Button
      size="small"
      shape="circle"
      onClick={onClick}
      icon={<EyeOutlined />}
      style={{ color: COLOR_PRIMARY, borderColor: COLOR_PRIMARY }}
    />
  </Tooltip>
);
